// src/services/licenseService.js
import { apiRequest } from './api';

export const licenseService = {
    /**
     * Get license pricing list
     */
    getLicensePricing: async (countryCode = null) => {
        try {
            const params = {};
            if (countryCode) params.country_code = countryCode;

            const response = await apiRequest.get('/payments/license-pricing', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch license pricing');
        }
    },

    /**
     * Calculate license cost for specific staff count
     */
    calculateLicenseCost: async (staffCount, orgId = null, durationMonths = 1) => {
        try {
            const params = {
                staff_count: staffCount,
                duration_months: durationMonths
            };
            if (orgId) params.org_id = orgId;

            const response = await apiRequest.get('/payments/calculate-cost', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to calculate license cost');
        }
    },

    /**
     * Request discount for organization
     */
    requestDiscount: async (percentage, reason, orgId = null, invoiceContext = null) => {
        try {
            const data = {
                percentage,
                reason
            };
            if (orgId) data.org_id = orgId;
            if (invoiceContext) data.invoice_context = invoiceContext;

            const response = await apiRequest.post('/payments/request-discount', data);
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to request discount');
        }
    },

    /**
     * Get selected staff for license
     */
    getSelectedStaff: async (orgId = null) => {
        try {
            const params = {};
            if (orgId) params.org_id = orgId;

            const response = await apiRequest.get('/payments/get-selected-staff', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch selected staff');
        }
    },

    /**
     * Update staff selection for license
     */
    updateStaffSelection: async (staffIds, orgId = null) => {
        try {
            const data = {
                staff_ids: staffIds
            };
            if (orgId) data.org_id = orgId;

            const response = await apiRequest.post('/payments/update-staff-selection', data);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update staff selection');
        }
    },

    /**
     * Initiate license payment
     */
    initiateLicensePayment: async (paymentData) => {
        try {
            const response = await apiRequest.post('/payments/initiate-payment', paymentData);
            // Handle both wrapped and unwrapped responses
            return response.data.data || response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to initiate payment');
        }
    },

    /**
     * Confirm manual payment made (Bank Transfer)
     */
    confirmManualPayment: async (paymentId) => {
        try {
            const response = await apiRequest.post('/payments/confirm-manual-payment', {
                payment_id: paymentId
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to confirm payment');
        }
    },

    /**
     * Activate license (Core Admin only)
     */
    activateLicense: async (paymentId) => {
        try {
            const response = await apiRequest.post('/payments/activate-license', {
                payment_id: paymentId
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to activate license');
        }
    },

    /**
     * Core Admin: Pay for organization
     */
    adminPayForOrganization: async (orgId, staffCount, notes = '') => {
        try {
            const response = await apiRequest.post('/payments/admin-pay-for-org', {
                org_id: orgId,
                staff_count: staffCount,
                notes
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to process payment');
        }
    },

    /**
     * Validate staff upload count against license
     */
    validateStaffUpload: async (uploadCount, orgId = null) => {
        try {
            const params = { count: uploadCount };
            if (orgId) params.org_id = orgId;

            const response = await apiRequest.get('/payments/validate-staff-upload', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to validate upload');
        }
    },

    /**
     * Get discount requests (Core Admin only)
     */
    getDiscountRequests: async (status = null) => {
        try {
            const params = {};
            if (status) params.status = status;
            const response = await apiRequest.get('/discounts/requests', { params });
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to get discount requests');
        }
    },

    /**
     * Process discount request (Core Admin only)
     */
    processDiscountRequest: async (requestId, status, approvedPercentage = null) => {
        try {
            const response = await apiRequest.post('/payments/process-discount', {
                request_id: requestId,
                status,
                approved_percentage: approvedPercentage
            });
            return response.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to process discount request');
        }
    },

    /**
     * Get license status for organization (for staff upload validation)
     */
    getLicenseStatus: async (orgId = null) => {
        try {
            const params = {};
            if (orgId) params.org_id = orgId;

            const response = await apiRequest.get('/subscriptions/license-status', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch license status');
        }
    }
};
